import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { invalidateEntitlements } from "@/hooks/useEntitlements";
import { invalidateMemories } from "@/hooks/useMemories";
import { useMemorySaved } from "@/hooks/useMemorySaved";
import type { ProcessorResult } from "@/lib/captureProcessor";

type Phase = "processing" | "error";

interface ProcessingScreenProps {
  /** Async save pipeline. Returns the memoryId + type used by the shared
   *  post-save fan-out (onMemorySaved, feed refresh). */
  processor: () => Promise<ProcessorResult>;
  onComplete: (memoryId: string) => void;
  onClose: () => void;
}

const ProcessingScreen = ({
  processor,
  onComplete,
  onClose,
}: ProcessingScreenProps) => {
  const { t } = useTranslation();
  const { onMemorySaved } = useMemorySaved();

  const [phase, setPhase] = useState<Phase>("processing");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const runningRef = useRef(false);
  const savedMemoryIdRef = useRef<string | null>(null);

  // Stabilise the processor reference so retries pick up the latest prop
  // without re-triggering the mount effect (which has an empty dep array).
  const processorRef = useRef(processor);
  processorRef.current = processor;

  const runProcessing = async () => {
    if (runningRef.current) return;
    if (savedMemoryIdRef.current) {
      // Memory already saved — skip re-processing on retry; just advance.
      onComplete(savedMemoryIdRef.current);
      return;
    }
    runningRef.current = true;

    setPhase("processing");
    setErrorMsg(null);

    try {
      const result = await processorRef.current();

      savedMemoryIdRef.current = result.memoryId;

      if (result.forcedSkip) {
        toast(t("memory.processing.savedWithoutAi"));
      }

      if (!result.skipFanout) {
        void invalidateMemories();
        void invalidateEntitlements();

        // Streak + acorns — non-critical: memory is already saved, so a failure
        // here must not surface the error state (which would invite a duplicate retry).
        try {
          await onMemorySaved(result.memoryId, {
            memoryType: result.memoryType,
            contentLibraryId: result.contentLibraryId ?? null,
          });
        } catch (streakErr) {
          console.error("[ProcessingScreen] streak/acorn update failed (non-fatal):", streakErr);
          Sentry.captureException(streakErr, { tags: { flow: "memory-saved-streak" } });
        }
      }

      onComplete(result.memoryId);
    } catch (err) {
      console.error("[ProcessingScreen]", err);
      Sentry.captureException(err, { tags: { flow: "memory-processing" } });
      setErrorMsg(err instanceof Error ? err.message : t("memory.processing.error"));
      setPhase("error");
    } finally {
      runningRef.current = false;
    }
  };

  useEffect(() => {
    void runProcessing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col items-center justify-between px-6 py-10 bg-background"
    >
      {/* Top section */}
      <div className="w-full max-w-sm flex flex-col items-center gap-4 pt-8">
        <span className="text-2xl leading-none" aria-hidden>
          🐿️
        </span>

        {phase === "error" ? (
          <p className="text-[13px] font-medium text-destructive">
            {t("memory.processing.error")}
          </p>
        ) : (
          <p className="text-[13px] font-medium text-foreground text-center">
            {t("memory.processing.status.default")}
          </p>
        )}

        {phase === "processing" && (
          <div className="h-1.5 w-24 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full w-1/2 rounded-full animate-pulse"
              style={{ backgroundColor: "#7A9A7C" }}
            />
          </div>
        )}

        {errorMsg && (
          <p className="text-xs text-muted-foreground text-center max-w-[260px]">
            {errorMsg}
          </p>
        )}
      </div>

      {/* Middle section — reserved for MVP 2 dynamic content */}
      <div className="w-full max-w-sm flex-1" />

      {/* Bottom section — error CTAs only; success auto-advances */}
      <div className="w-full max-w-sm flex flex-col items-center gap-3 pb-2">
        {phase === "error" && (
          <div className="flex gap-3 w-full">
            <Button variant="outline" className="flex-1 h-11" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button className="flex-1 h-11" onClick={runProcessing}>
              {t("common.tryAgain")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessingScreen;
